// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@adl/App/Store/Selectors/SettingsSelectors";
import { ButtonProps } from "@adl/MobiusOverrides/Button/Button";
import { color, getCloudinaryUrl, xScreen } from "@adl/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonVariants } from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    imageUrl: string;
    title: string;
    description: string;
    buttonTitle?: string;
    buttonLink: LinkFieldValue;
    backgroundColor: string;
    contentPosition: "flex-start" | "flex-end";
}

type Props = OwnProps & HasHistory;
export interface ShowcaseContainerStyles {
    cardWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    buttonStyle?: ButtonProps;
    titleStyle?: TypographyProps;
    contentStyle?: TypographyProps;
}

export const showcaseContainerStyles: ShowcaseContainerStyles = {
    cardWrapper: {
        css: css`
            max-width: 650px;
            background-image: ${({ imageUrl }: any) => `url(${imageUrl})`};
            background-repeat: no-repeat;
            background-size: 650px 100%;
            display: flex;
            justify-content: ${({ contentPosition }: any) => `${contentPosition}`};
            flex: 0 0 100%;
            min-height: 300px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
                background-size: 100%;
            }
            ${xScreen("lte", "sm")} {
                max-width: 100%;
                background-position: center;
                background-size: cover;
            }
        `,
    },
    contentWrapper: {
        css: css`
            max-width: 70%;
            padding: 24px 24px 45px;
            background: ${({ backgroundColor }: any) => `${backgroundColor}`};
            ${xScreen("lte", "md")} {
                max-width: 55%;
            }
            ${xScreen("lte", "xs")} {
                max-width: unset;
                padding: 16px;
            }
        `,
    },
    buttonStyle: {
        sizeVariant: "medium",
        color: "common.white",
        typographyProps: {
            color: "primary.main",
        },
        css: css`
            &,
            &&&:hover,
            &&&:focus {
                border: 1px solid ${getColor("common.white")};
                background: ${getColor("common.white")};
            }
            ,
            &&&:hover span,
            &&&focus span {
                color: ${getColor("common.ui1")};
            }
        `,
    },
    titleStyle: {
        variant: "h4",
        css: css`
            margin-bottom: 16px;
            ${TypographyStyle} {
                font-size: 20px;
                color: ${color("common.white")};
                font-weight: bold;
                letter-spacing: 0;
                line-height: 25px;
                margin: 0;
            }
        `,
    },
    contentStyle: {
        variant: "p",
        css: css`
            margin-bottom: 24px;
            ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 26px;
                color: ${color("common.white")};
                margin: 0;
            }
        `,
    },
};

const styles = showcaseContainerStyles;

const ShowcaseCard = ({
    imageUrl,
    title,
    description,
    buttonTitle,
    buttonLink,
    backgroundColor,
    contentPosition,
}: Props) => {
    const { url, title: linkTitle } = useGetLink(buttonLink);
    const history = useHistory();
    const { cloudinaryBaseUrl } = useAppSelector(state => ({
        cloudinaryBaseUrl: getSettingsCollection(state).cloudinarySettings.cloudinaryRootUrl,
    }));

    const handleButtonClick = () => {
        if (!url) {
            return;
        }
        history.push(url);
    };

    return (
        <StyledWrapper
            {...styles.cardWrapper}
            {...{
                imageUrl: getCloudinaryUrl(imageUrl, cloudinaryBaseUrl, {
                    width: 637,
                }),
                contentPosition,
            }}
        >
            <StyledWrapper {...styles.contentWrapper} {...{ backgroundColor }}>
                <Typography {...styles.titleStyle} as="div">
                    {parse(title, parserOptions)}
                </Typography>
                <Typography {...styles.contentStyle} as="div">
                    {parse(description, parserOptions)}
                </Typography>
                <Button {...styles.buttonStyle} sizeVariant="medium" onClick={handleButtonClick}>
                    {translate(buttonTitle || linkTitle || "")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withHistory(ShowcaseCard);
